import { Gym, IVideoToCache } from 'fitbeat.models';
import {
  getDefaultExerciseVersion, GymSchedulingConfig,
  isStaticCardioZone, isStaticFunctionalZone,
  ZoneMapper, ZoneMappings,
} from '../../config';
import {
  CardioOrResistance,
  Exercise,
  ExerciseFormat,
  ExerciseSessionType,
  IGymStateSessionViewModel,
  SessionSequence,
  Side,
  UserWorkoutSession,
  UserWorkoutStatus,
  Zone,
} from '../../entities';
import { SessionSequenceStrategyContext } from '../sessionsequence';
import { ScreenSession } from './screenSession';
import { TimeSlices } from './timeslices';

export interface IZoneFallbackExercise {
  name: string;
  uniqueReferenceNumber: string;
}

export interface IZoneFallback {
  format: ExerciseFormat;
  exercise: IZoneFallbackExercise;
  alternateExercise: IZoneFallbackExercise;
  secondAlternateExercise?: IZoneFallbackExercise;
  thirdAlternateExercise?: IZoneFallbackExercise;
}

export interface IZone {
  left: IZoneFallback;
  right: IZoneFallback;
}

export interface IZonefallbackData {
  [key: string]: IZone;
}

export interface ISignedTransitionVideosMap {
  [key: string]: string;
}

export class ZoneFallback {
  public static createOfflineSession(stationNumber: number, cardioOrResistance: CardioOrResistance, gym: Gym)
    : IGymStateSessionViewModel {
    const zone = Zone.zoneForStation(stationNumber, gym);

    return {
      userProfile: {
        name: ZoneFallback.getNameForWorkout(cardioOrResistance),
        fullname: ZoneFallback.getNameForWorkout(cardioOrResistance),
        userWorkoutStatus: UserWorkoutStatus.Other,
        uid: '',
      },
      workoutSessionId: null,
      removedAt: null,
      checkedIn: true,
      workoutType: NaN,
      amountOfStages: NaN,
      currentStage: NaN,
      cycleTimeInSeconds: GymSchedulingConfig.cycleTimeInSeconds,
      sessionSequence: [],
      workoutDescription: UserWorkoutSession.WorkoutDescriptionMap.get(cardioOrResistance) || '',
      bookingState: null,
      format: ExerciseFormat.Burn2Endurance,
      areaCategory: zone.areaCategory,
      zoneName: zone.name,
      zoneNumber: zone.numberInGym,
      displayNumber: zone.displayNumber,
      nextZoneNumber: zone.nextZoneNumber,
      type: ExerciseSessionType.Offline,
      isEmpty: false,
      isNewUser: false,
      side: Side.Left,
      stationNumber: NaN,
    };
  }

  private static getNameForWorkout = (
    cardioOrResistance: CardioOrResistance,
  ) => (cardioOrResistance === CardioOrResistance.Cardio
    ? 'Burn Workout'
    : 'Build Workout');

  constructor(
    private zoneFallbackData: () => IZonefallbackData,
    private gymName: string,
    private logger: any,
    private signedTransitionVideosMap?: ISignedTransitionVideosMap,
  ) {}

  public getFallbackSessionForZone = (zoneNumberString: string, now: Date, gym: Gym): ScreenSession => {
    const zoneNumber = Number(zoneNumberString);
    const stationNumber = Zone.firstStationForZoneNumber(zoneNumber);
    const timeSlices = new TimeSlices(now, 120);
    const currentSlice = timeSlices.previousSliceClosestToTime(new Date(now.getTime() + 1000)) || new Date();
    const loop = this.getLoop({ numerOfZones: gym.numOfZones, currentSlice });
    const currentWorkoutStage = this.getWorkoutStage(currentSlice) - 1;
    const zoneType = ZoneMappings.getZoneTypeForOfflineHydration(zoneNumber, gym, loop);
    const shouldUseThirdAndFourthExercisesInStudio = this
      .calculateShouldUseThirdAndFourthExercisesInStudio(gym, loop, currentWorkoutStage, zoneType, zoneNumber);
    this.logger.info(`getting fallback session for zone ${zoneNumber} type ${zoneType}`);

    if (isStaticCardioZone(zoneType)) {
      return new ScreenSession(
        this.gymName,
        ZoneFallback.createOfflineSession(stationNumber, CardioOrResistance.Cardio, gym),
        ZoneFallback.createOfflineSession(stationNumber, CardioOrResistance.Cardio, gym),
        [], // no audio
        currentSlice.getTime(),
        currentSlice.getTime() + 120000,
        '',
      );
    }

    const fallbackExercises = this.getExercisesForZone(zoneType);
    if (!fallbackExercises) {
      throw new Error(`No zone fallback data yet for zoneType ${zoneType}`);
    }

    const { left, right } = fallbackExercises;
    const leftZoneFallbackExercise = this.getFirstExercise(left, shouldUseThirdAndFourthExercisesInStudio);
    const leftZoneFallbackAlternateExercise = this.getSecondExercise(
      left,
      shouldUseThirdAndFourthExercisesInStudio,
    );
    const rightZoneFallbackExercise = this.getFirstExercise(right, shouldUseThirdAndFourthExercisesInStudio);
    const rightZoneFallbackAlternateExercise = this.getSecondExercise(
      right,
      shouldUseThirdAndFourthExercisesInStudio,
    );
    const leftExercise = this.getExercise(leftZoneFallbackExercise);
    const leftAlternativeExercise = this.getExercise(leftZoneFallbackAlternateExercise);
    const rightExercise = this.getExercise(rightZoneFallbackExercise);
    const rightAlternativeExercise = this.getExercise(rightZoneFallbackAlternateExercise);
    const leftSession = ZoneFallback.createOfflineSession(stationNumber, CardioOrResistance.Cardio, gym);
    const rightSession = ZoneFallback.createOfflineSession(stationNumber, CardioOrResistance.Resistance, gym);
    const session = new ScreenSession(
      this.gymName,
      leftSession,
      rightSession,
      [], // no audio in fallback, since it can be annoying
      currentSlice.getTime(),
      currentSlice.getTime() + 120000,
      '',
    );

    session.leftStationIsEmpty = false;
    session.rightStationIsEmpty = false;

    const burnCombo = SessionSequenceStrategyContext.getSessionSequence(
      left.format,
      leftExercise,
      leftAlternativeExercise,
    );

    const buildCombo = SessionSequenceStrategyContext.getSessionSequence(
      right.format,
      rightExercise,
      rightAlternativeExercise,
    );

    if (this.signedTransitionVideosMap) {
      this.replaceVideoFileWithSignedURL(burnCombo, this.signedTransitionVideosMap);
      this.replaceVideoFileWithSignedURL(buildCombo, this.signedTransitionVideosMap);
    }

    session.leftStationInZone.sessionSequence = burnCombo;
    session.rightStationInZone.sessionSequence = buildCombo;
    return session;
  };

  public replaceVideoFileWithSignedURL(
    sessionSequences: SessionSequence[],
    signedTransitionVideosMap: ISignedTransitionVideosMap,
  ) {
    sessionSequences.forEach((sessionSequence) => {
      sessionSequence.sequence.forEach((sequenceEntry) => {
        if (signedTransitionVideosMap[sequenceEntry.videoFile]) {
          sequenceEntry.videoFile = signedTransitionVideosMap[sequenceEntry.videoFile];
        }
      });
    });
  }

  public getAllFallbackExerciseReferences = (): string[] => {
    const outputArray: any[] = [];
    const fallbackData = this.zoneFallbackData();
    Object.keys(fallbackData).forEach((zoneNumber: any) => {
      const zoneExerciseData = fallbackData[zoneNumber];
      const { left, right } = zoneExerciseData;
      const leftExerciseUniqueRef = left.exercise.uniqueReferenceNumber;
      const leftAlternateExerciseUniqueRef = left.alternateExercise.uniqueReferenceNumber;
      const leftSecondAlternateExerciseUniqueRef = left.secondAlternateExercise?.uniqueReferenceNumber;
      const leftThirdAlternateExerciseUniqueRef = left.thirdAlternateExercise?.uniqueReferenceNumber;
      const rightExerciseUniqueRef = right.exercise.uniqueReferenceNumber;
      const rightAlternateExerciseUniqueRef = right.alternateExercise.uniqueReferenceNumber;
      const rightSecondAlternateExerciseUniqueRef = right.secondAlternateExercise?.uniqueReferenceNumber;
      const rightThirdAlternateExerciseUniqueRef = right.thirdAlternateExercise?.uniqueReferenceNumber;
      outputArray.push(leftExerciseUniqueRef);
      outputArray.push(leftAlternateExerciseUniqueRef);
      outputArray.push(leftSecondAlternateExerciseUniqueRef);
      outputArray.push(leftThirdAlternateExerciseUniqueRef);
      outputArray.push(rightExerciseUniqueRef);
      outputArray.push(rightAlternateExerciseUniqueRef);
      outputArray.push(rightSecondAlternateExerciseUniqueRef);
      outputArray.push(rightThirdAlternateExerciseUniqueRef);
    });

    outputArray.push.apply(outputArray, SessionSequence.getAllVideos());
    const onlyUniqueReferences = outputArray.reduce((uniqueReferences: string[], reference: string) => {
      if (uniqueReferences.indexOf(reference) < 0) {
        uniqueReferences.push(reference);
      }
      return uniqueReferences;
    }, []);

    return onlyUniqueReferences;
  };

  public getExercise = (zoneFallbackExercise: IZoneFallbackExercise): Exercise => {
    const { name, uniqueReferenceNumber } = zoneFallbackExercise;
    this.logger.trace(`creating exercise with name ${name}`);
    const exercise = new Exercise(name, '', [], false, [CardioOrResistance.Cardio], [], [], []);
    exercise.uniqueReferenceNumber = uniqueReferenceNumber;
    return exercise;
  };

  public getWorkoutStage = (currentSlice: Date): number => {
    const hours = currentSlice.getHours();
    const minutes = currentSlice.getMinutes();
    const minutesSinceMidnight = hours * 60 + minutes;
    const stage = Math.floor(minutesSinceMidnight / 2);
    return stage + 1;
  };

  public getLoop({
    numerOfZones,
    currentSlice,
  }: {
    numerOfZones: number;
    currentSlice: Date;
  }): number {
    const stage = this.getWorkoutStage(currentSlice) - 1;
    return Math.floor(stage / numerOfZones);
  }

  public async mapFallbackDataToPlayableUrls(
    zoneNumberInGym: number,
    gym: Gym,
    mapReferenceToSignedCdnUrl: (videoInfo: IVideoToCache) => Promise<string>,
  ) {
    const createSignedUrlForFallback = async (exercise: IZoneFallbackExercise | undefined) => {
      if (!exercise) {
        return;
      }
      exercise.uniqueReferenceNumber = await mapReferenceToSignedCdnUrl({
        reference: exercise.uniqueReferenceNumber,
        version: getDefaultExerciseVersion(),
      });
    };

    const zoneTypesForNumberIngym = new ZoneMapper().getZoneTypesForNumberInGym(
      +zoneNumberInGym,
      gym,
    );
    const zoneFallbackDataCopy: IZonefallbackData = JSON.parse(JSON.stringify(this.zoneFallbackData()));
    const zones = Object.keys(zoneFallbackDataCopy);
    for (const zone of zones) {
      const zoneTypeNotPossibleForThisZone = zoneTypesForNumberIngym.indexOf(+zone) === -1;
      if (zoneTypeNotPossibleForThisZone) {
        delete zoneFallbackDataCopy[zone];
      } else {
        const fallback = zoneFallbackDataCopy[zone];
        await createSignedUrlForFallback(fallback.left.exercise);
        await createSignedUrlForFallback(fallback.left.alternateExercise);
        await createSignedUrlForFallback(fallback.left.secondAlternateExercise);
        await createSignedUrlForFallback(fallback.left.thirdAlternateExercise);

        await createSignedUrlForFallback(fallback.right.exercise);
        await createSignedUrlForFallback(fallback.right.alternateExercise);
        await createSignedUrlForFallback(fallback.right.secondAlternateExercise);
        await createSignedUrlForFallback(fallback.right.thirdAlternateExercise);
      }
    }
    return zoneFallbackDataCopy;
  }

  public async getSignedTransitionVideosMap(
    mapReferenceToSignedCdnUrl: (videoInfo: IVideoToCache) => Promise<string>,
  ): Promise<ISignedTransitionVideosMap> {
    const videos: IVideoToCache[] = SessionSequence.getAllVideos().map(
      (fallbackReference: string): IVideoToCache => ({ reference: fallbackReference }),
    );
    const transitionVideosMap: { [key: string]: string } = {};
    for (const video of videos) {
      transitionVideosMap[video.reference] = await mapReferenceToSignedCdnUrl(video);
    }
    return transitionVideosMap;
  }

  private getExercisesForZone = (zoneType: number) => {
    const fallbackData = this.zoneFallbackData();
    if (!fallbackData) {
      return;
    }
    return this.zoneFallbackData()[zoneType];
  };

  private getFirstExercise = (zoneFallBack: IZoneFallback, shouldUseThirdAndFourthExercisesInStudio: boolean) => {
    if (zoneFallBack.secondAlternateExercise && shouldUseThirdAndFourthExercisesInStudio) {
      return zoneFallBack.secondAlternateExercise;
    }
    return zoneFallBack.exercise;
  };

  private getSecondExercise = (zoneFallBack: IZoneFallback, shouldUseThirdAndFourthExercisesInStudio: boolean) => {
    if (zoneFallBack.thirdAlternateExercise && shouldUseThirdAndFourthExercisesInStudio) {
      return zoneFallBack.thirdAlternateExercise;
    }
    return zoneFallBack.alternateExercise;
  };

  private calculateShouldUseThirdAndFourthExercisesInStudio = (
    gym: Gym,
    currentLoop: number,
    currentWorkoutStage: number,
    zoneType: number,
    zoneNumber: number,
  ) => {
    if (isStaticFunctionalZone(zoneType)) {
      // use the 2nd and 3rd alternate in the next time slice
      return currentWorkoutStage % 2 === 1;
    }

    let totalNumfOfRepeatingZoneTypesBeforeCurrentZone = 0;
    for (let loop = 0; loop < currentLoop; loop++) {
      for (let numberInGym = 0; numberInGym < gym.numOfZones; numberInGym++) {
        const zoneTypeForZoneNumber = ZoneMappings.getZoneTypeForOfflineHydration(
          numberInGym + 1,
          gym,
          loop,
        );
        if (zoneType === zoneTypeForZoneNumber) {
          totalNumfOfRepeatingZoneTypesBeforeCurrentZone += 1;
        }
      }
    }
    const numOfZoneTypeUsedInCurrentZone = gym.zoneMapping.reduce(
      (acc, cur, index) => {
        const indexOfCurrentZoneType = zoneNumber - 1;
        const zoneTypeForPrevZoneNumber = ZoneMappings.getZoneTypeForOfflineHydration(
          index + 1,
          gym,
          currentLoop,
        );
        const hasAlreadyUsedZoneTypeInCurrentZone = zoneTypeForPrevZoneNumber === zoneType
            && indexOfCurrentZoneType > index;
        if (hasAlreadyUsedZoneTypeInCurrentZone) {
          acc += 1;
        }
        return acc;
      },
      0,
    );
    const totalNumberOfRepeatingZoneTypes = totalNumfOfRepeatingZoneTypesBeforeCurrentZone
        + numOfZoneTypeUsedInCurrentZone;
    const shouldUseAlternativeExercisesInStudio = totalNumberOfRepeatingZoneTypes % 2 > 0;
    return shouldUseAlternativeExercisesInStudio;
  };
}
